<template>
  <div
    class="adn-select-option"
    :class="{
      'adn-select-option--selected': preselected && !option.disabled,
      'adn-select-option--disabled': option.disabled,
    }"
    tabindex="0"
    @click.stop="onClick"
    @focusin="onFocusEnter"
  >
    <adn-checkbox
      v-if="showCheckbox"
      ref="checkbox"
      class="adn-select-option__checkbox"
      :checked="selected"
      :disabled="option.disabled"
      @change="onCheckboxChange"
    />
    <span v-if="showIcon" class="adn-select-option__icon adn-icon" :class="iconClassNames" aria-hidden="true" />
    <div v-if="showImage" class="adn-select-option__image" :class="hasImage ? '' : `${option.defaultImageClass}`">
      <adn-image v-if="hasImage" class="adn-select-option__image-thumbnail" :source="option.image" alt="" />
    </div>
    <div v-if="showText" class="adn-select-option__text">
      <slot name="content">
        <span v-if="hasText" class="adn-select-option__text-main" v-html="option.text" />
        <span v-if="hasTextComplement" class="adn-select-option__text-secondary">
          {{ option.textComplement }}
        </span>
      </slot>
    </div>
  </div>
</template>

<script>
import AdnCheckbox from '../AdnCheckbox/AdnCheckbox.vue'
import AdnImage from '../AdnImage/AdnImage.vue'

export default {
  name: 'AdnSelectOption',
  components: { AdnCheckbox, AdnImage },
  props: {
    option: {
      type: Object,
      required: true,
    },
    selected: {
      type: Boolean,
      default: false,
    },
    preselected: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['click', 'focusenter', 'keyup'],
  computed: {
    showCheckbox() {
      return this.option.checkbox === true
    },
    showIcon() {
      return this.option.iconName && this.option.iconName.length > 0
    },
    showImage() {
      return this.hasImageSlot || this.hasImage || this.option.defaultImageClass
    },
    showText() {
      return this.hasTextSlot || this.hasText || this.hasTextComplement
    },
    iconClassNames() {
      return (this.option.iconName && [`adn-icon--${this.option.iconName}`]) || []
    },
    hasImageSlot() {
      return !!this.$slots.image
    },
    hasTextSlot() {
      return !!this.$slots.text
    },
    hasImage() {
      return this.option.image
    },
    hasText() {
      return this.option.text
    },
    hasTextComplement() {
      return this.option.textComplement
    },
  },
  methods: {
    onFocusEnter() {
      this.$emit('focusenter')
    },
    onClick() {
      if (this.option.disabled) {
        return
      }
      if (this.option.checkbox) {
        this.$refs.checkbox.onClick()
      } else {
        this.$emit('click')
      }
    },
    onCheckboxChange() {
      if (this.option.disabled) {
        return
      }
      this.$emit('click')
    },
  },
}
</script>

<style scoped lang="scss">
.adn-select-option {
  display: flex;
  justify-content: space-between;
  background-color: var(--idf-color-neutral-100);
  overflow: hidden;
  width: 100%;
  min-height: 4.4rem;
  height: fit-content;
  cursor: pointer;

  &--disabled {
    color: var(--color-inactive-light);

    &:hover {
      cursor: default;
    }
  }

  &--selected,
  &:hover {
    background-color: var(--idf-color-neutral-300);
  }

  &__checkbox {
    flex-shrink: 0;
    padding: 0 1rem;
    width: 4rem;
    height: 4.4rem;
  }

  &__icon {
    flex-shrink: 0;
    vertical-align: middle;
    text-align: center;
    width: 4.4rem;
    height: auto;
  }

  &__image {
    flex-shrink: 0;
    vertical-align: middle;
    text-align: center;
    width: 4.4rem;
    height: 4.4rem;
  }

  &__image-thumbnail {
    background-color: var(--idf-color-neutral-100);
    border-radius: var(--corner-radius-xs);
    margin: 0.2rem;
    display: block;
    width: 4rem;
    height: 4rem;
    object-fit: contain;
  }

  &__text {
    display: flex;
    flex-grow: 1;
    text-align: left;
    height: 100%;
    line-height: 2rem;
    padding: 1.2rem 1rem; // = (.adn-select-option.min-height - line-height) / 2

    span > mark {
      padding: 0;
      font-weight: bold;
      background-color: transparent;
    }
  }

  &__checkbox + &__text {
    padding-left: 0;
  }

  &__text-main {
    font-weight: normal;
  }

  &__text-secondary {
    font-weight: normal;
    color: var(--color-text-light);
    padding-left: 5px;
  }
}
</style>
